import React, { useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { useFocusedRow } from "hooks/useFocusedRecord";
import {
  FocusNotFound,
  focusRowClass,
  scrollRowIntoView,
} from "Common/FocusedRow";
import { Users2, Search, RefreshCw } from "lucide-react";
import { ToastContainer } from "react-toastify";
import usePlacements from "hooks/usePlacements";
import { AdminPlacement } from "services/placement.service";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  completed: "bg-blue-100 text-blue-800",
  terminated: "bg-red-100 text-red-800",
  disputed: "bg-orange-100 text-orange-700",
};

const Placements = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const { data: response, isLoading, isError, refetch } = usePlacements({
    page,
    limit: 20,
    status: statusFilter || undefined,
  });

  const placements: AdminPlacement[] = response?.data?.data ?? [];

  // Followed here from a placement alert. This list has no per-record route, so the id
  // arrives as `?focus=` and rings the row it names.
  const { notFound: focusNotFound, dismissFocus, isFocused } = useFocusedRow(
    placements,
    isLoading
  );
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const filtered = search
    ? placements.filter((p) => {
        const q = search.toLowerCase();
        return (
          p.worker?.firstName?.toLowerCase().includes(q) ||
          p.worker?.lastName?.toLowerCase().includes(q) ||
          p.worker?.phoneNumber?.includes(q) ||
          p.agency?.name?.toLowerCase().includes(q) ||
          p.employer?.firstName?.toLowerCase().includes(q) ||
          p.employer?.lastName?.toLowerCase().includes(q)
        );
      })
    : placements;

  return (
    <React.Fragment>
      <BreadCrumb title="Placements" pageTitle="Agency" />
      <ToastContainer closeButton={false} limit={1} />

      {focusNotFound && <FocusNotFound onDismiss={dismissFocus} />}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter this page: worker, employer, agency..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:w-44"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="terminated">Terminated</option>
            <option value="disputed">Disputed</option>
          </select>

          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{total} total</span>
        </div>

        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Worker</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Employer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Agency</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Salary</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Start Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={6} label="Couldn't load placements" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">Loading...</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users2 className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No placements found</p>
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr
                    key={p._id}
                    ref={scrollRowIntoView(isFocused(p._id))}
                    className={`hover:bg-gray-50 transition-colors ${focusRowClass(
                      isFocused(p._id)
                    )}`}
                  >
                    <td data-label="Worker" data-cell="title" className="px-4 py-3">
                      <p className="font-medium text-gray-900">
                        {p.worker?.firstName} {p.worker?.lastName}
                      </p>
                      <p className="text-xs text-gray-400">{p.worker?.phoneNumber}</p>
                    </td>
                    <td data-label="Employer" className="px-4 py-3 text-gray-700">
                      {p.employer?.firstName} {p.employer?.lastName}
                    </td>
                    <td data-label="Agency" className="px-4 py-3 text-gray-600">{p.agency?.name ?? "—"}</td>
                    <td data-label="Salary" className="px-4 py-3 text-gray-700">
                      ₦{p.salary?.toLocaleString()}
                    </td>
                    <td data-label="Start Date" className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {p.startDate ? new Date(p.startDate).toLocaleDateString() : "—"}
                    </td>
                    <td data-label="Status" className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] ?? "bg-gray-100 text-gray-600"}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Placements;
